import { Badge } from "../../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { cn } from "../../utils/common/cn";
import { StatusBadge } from "./StatusBadge";
import {
  pct,
  thr,
  usdMSigned,
} from "./format";
import type { ConcentrationRow } from "../../utils/portfolio-risk-concentration/api/types";

/**
 * The member breakdown for a joint — what makes the governing choice auditable on screen.
 *
 * Shared by the Monitor and Capacity sheets, so a joint explains itself the same way wherever
 * it is opened.
 *
 * ── Three badges, because one member governs and two facts can still be elsewhere ────────
 * A joint now takes every figure from its lowest-threshold member ("governs"). That is what
 * makes the row internally consistent, but it also means the row can be quieter than the
 * joint really is: another member may sit closer to its own threshold, or have less room to
 * grow. Both are flagged here rather than left to be inferred from the numbers, because this
 * table is the only place they surface at all.
 */
export function JointMembersTable({ row }: { row: ConcentrationRow }) {
  const members = row.memberRefs ?? [];
  if (!members.length) return null;

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
        Members · {members.length}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10px] uppercase tracking-[0.12em]">Member</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.12em]">Current / limit</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.12em]">Used</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.12em]">Headroom</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((m) => (
            <TableRow key={m.memberRef} className={cn(m.isBinding && "bg-primary/5")}>
              <TableCell className="align-top">
                <div className="font-medium">{m.limitDesc}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  {m.isBinding && (
                    <Badge
                      variant="outline"
                      className="border-primary/50 text-[9px] uppercase tracking-[0.1em] text-primary"
                    >
                      governs
                    </Badge>
                  )}
                  {/* Only when it is NOT the governing member — otherwise every joint would
                      carry three badges on one row saying the same thing. */}
                  {m.isTightestCapacity && !m.isBinding && (
                    <Badge
                      variant="outline"
                      className="border-destructive/50 text-[9px] uppercase tracking-[0.1em] text-destructive"
                    >
                      least room
                    </Badge>
                  )}
                  {m.isWorstMember && !m.isBinding && (
                    <Badge
                      variant="outline"
                      className="border-amber/50 text-[9px] uppercase tracking-[0.1em] text-amber"
                    >
                      closest to breach
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="align-top tabular-nums">
                {pct(m.currentDollarPct)}
                <span className="text-muted-foreground"> / {thr(m.thresholdPct)}</span>
              </TableCell>
              <TableCell className="align-top tabular-nums">
                {m.utilisationPct.toFixed(0)}%
              </TableCell>
              <TableCell className="align-top tabular-nums">
                <span className={cn(m.headroomDollars < 0 && "text-destructive")}>
                  {usdMSigned(m.headroomDollars)}
                </span>
                <div className="mt-0.5">
                  <StatusBadge status={m.status} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-[11px] text-muted-foreground">
        The joint is judged on its <strong>lowest threshold</strong> — the member marked
        &ldquo;governs&rdquo; — and every figure on the row above comes from that one limit.
        The same new dollars still have to satisfy every member at once, so a member marked
        &ldquo;least room&rdquo; caps new business before the governing one does.
      </p>
    </div>
  );
}
